import type { StaffMember } from "@kitchen/schemas";
import { Button, Chip, Field, Panel } from "@kitchen/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  api,
  approveDevice,
  generateEmergencyCodes,
  getEmergencyCodesCount,
  getPendingDevices,
  rejectDevice,
  type RoleRow,
} from "../api.js";
import { ErrorLine, useAction } from "./common.js";

/** Staff list: the only way anyone becomes staff (ADR-29). Roles are given here or in the role designer. */
export function StaffPage() {
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => api<StaffMember[]>("/staff") });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api<RoleRow[]>("/roles") });
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState("");
  const add = useAction(
    async (input: { phone: string; displayName: string; roleId: string }) => {
      const { id } = await api<{ id: string }>("/staff", {
        body: { phone: input.phone, displayName: input.displayName },
      });
      if (input.roleId) await api(`/roles/${input.roleId}/assign`, { body: { personId: id } });
      setPhone("");
      setName("");
    },
    [["staff"], ["roles"]],
  );
  const remove = useAction((id: string) => api(`/staff/${id}/remove`, { body: {} }), [["staff"]]);
  const staffRoles = (roles.data ?? []).filter((r) => r.context === "staff" && r.systemKey !== "support");
  const queryClient = useQueryClient();
  const emergencyCount = useQuery({
    queryKey: ["emergency-codes-count"],
    queryFn: getEmergencyCodesCount,
    retry: false,
  });
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const pendingDevices = useQuery({
    queryKey: ["pending-devices"],
    queryFn: getPendingDevices,
    retry: false,
  });
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [deviceActionError, setDeviceActionError] = useState<string | null>(null);

  const handleApprove = async (id: string) => {
    setProcessingId(id);
    setDeviceActionError(null);
    try {
      await approveDevice(id);
      await queryClient.invalidateQueries({ queryKey: ["pending-devices"] });
    } catch (err: unknown) {
      setDeviceActionError(err instanceof Error ? err.message : "خطا در تأیید دستگاه");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: string) => {
    setProcessingId(id);
    setDeviceActionError(null);
    try {
      await rejectDevice(id, "رد دستگاه توسط مدیر ارشد");
      await queryClient.invalidateQueries({ queryKey: ["pending-devices"] });
    } catch (err: unknown) {
      setDeviceActionError(err instanceof Error ? err.message : "خطا در رد دستگاه");
    } finally {
      setProcessingId(null);
    }
  };

  const handleGenerateCodes = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const res = await generateEmergencyCodes();
      setNewCodes(res.codes);
      await queryClient.invalidateQueries({ queryKey: ["emergency-codes-count"] });
    } catch (err: unknown) {
      setGenError(err instanceof Error ? err.message : "خطا در تولید کدهای ورود اضطراری");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="grid" style={{ gridTemplateColumns: "minmax(0,1fr) 360px", alignItems: "start" }}>
      <div className="stack">
        <Panel title="کارکنان">
          <div className="card" style={{ padding: "var(--s2) var(--s4)" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>نام</th>
                  <th>موبایل</th>
                  <th>PIN</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(staff.data ?? []).map((s) => (
                  <tr key={s.id}>
                    <td className="m">
                      <span className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
                        <span>{s.displayName}</span>
                        {s.isSupport && <Chip tone="blue">پشتیبان فنی</Chip>}
                      </span>
                    </td>
                    <td className="num" dir="ltr" style={{ textAlign: "start" }}>
                      {`0${s.phone.slice(3)}`}
                    </td>
                    <td>{s.hasPin ? <Chip tone="ok">ساخته شده</Chip> : <Chip plain>هنوز وارد نشده</Chip>}</td>
                    <td className="end">
                      {s.isSupport ? (
                        <span className="muted t-cap">این دسترسی فقط از سرور تغییر می‌کند</span>
                      ) : (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.run(s.id);
                          }}
                          disabled={remove.busy}
                        >
                          حذف از فهرست کارکنان
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ErrorLine error={remove.error} />
        </Panel>

        {pendingDevices.data !== undefined && (
          <Panel title="دستگاه‌های در انتظار تأیید">
            <div className="card stack" style={{ padding: "var(--s4)" }}>
              {pendingDevices.data.length === 0 ? (
                <p className="ink2" style={{ margin: 0 }}>
                  هیچ دستگاهی در انتظار تأیید نیست.
                </p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>نام کاربر</th>
                        <th>شماره</th>
                        <th>نام دستگاه</th>
                        <th>سیستم‌عامل</th>
                        <th>زمان درخواست</th>
                        <th>عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingDevices.data.map((d) => (
                        <tr key={d.id} data-testid={`pending-device-row-${d.id}`}>
                          <td className="b">{d.personName}</td>
                          <td dir="ltr" style={{ textAlign: "right" }}>
                            {d.phoneTail ? `...${d.phoneTail}` : "—"}
                          </td>
                          <td>{d.label}</td>
                          <td>
                            <Chip plain>{d.platform}</Chip>
                          </td>
                          <td className="ink2" style={{ fontSize: "0.85rem" }}>
                            {new Date(d.createdAt).toLocaleTimeString("fa-IR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          <td>
                            <div className="row" style={{ gap: "var(--s2)" }}>
                              <Button
                                variant="primary"
                                onClick={() => {
                                  void handleApprove(d.id);
                                }}
                                disabled={processingId === d.id}
                              >
                                تأیید
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  void handleReject(d.id);
                                }}
                                disabled={processingId === d.id}
                              >
                                رد
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <ErrorLine error={deviceActionError} />
            </div>
          </Panel>
        )}

        {emergencyCount.data !== undefined && (
          <Panel title="کدهای ورود اضطراری">
            <div className="card stack" style={{ padding: "var(--s4)" }}>
              <p className="ink2" style={{ margin: 0 }}>
                کدهای یک‌بارمصرف برای ورود به سامانه‌ی کارکنان در صورت قطعی یا عدم دسترسی به پیامک.
              </p>
              <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <span>
                  تعداد کدهای معتبر باقی‌مانده: <strong className="b">{emergencyCount.data.count}</strong> از
                  ۱۰
                </span>
                <Button
                  variant="primary"
                  onClick={() => {
                    void handleGenerateCodes();
                  }}
                  disabled={generating}
                >
                  {generating ? "در حال تولید..." : "تولید ۱۰ کد جدید"}
                </Button>
              </div>
              <ErrorLine error={genError} />
            </div>
          </Panel>
        )}
      </div>
      <Panel title="افزودن به کارکنان">
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            add.run({ phone, displayName: name, roleId });
          }}
        >
          <Field
            id="staff-name"
            label="نام"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <Field
            id="staff-phone"
            label="شماره‌ی موبایل"
            dir="ltr"
            inputMode="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
            }}
            hint="با همین شماره از «ورود کارکنان» وارد می‌شود و PIN خودش را می‌سازد."
          />
          <div className="field">
            <label htmlFor="staff-role">نقش</label>
            <select
              id="staff-role"
              className="input"
              value={roleId}
              onChange={(e) => {
                setRoleId(e.target.value);
              }}
            >
              <option value="">بعداً تعیین می‌کنم</option>
              {staffRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="primary" icon="plus" disabled={add.busy || !name || !phone}>
            افزودن
          </Button>
          <ErrorLine error={add.error} />
        </form>
      </Panel>

      {newCodes && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="card stack printable-codes-modal"
            style={{
              background: "var(--bg-panel, #fff)",
              maxWidth: 550,
              width: "90%",
              padding: "var(--s4)",
              borderRadius: "var(--r2)",
            }}
          >
            <h3 className="b" style={{ margin: 0 }}>
              کدهای ورود اضطراری (یک‌بار مصرف)
            </h3>
            <p className="ink2" style={{ fontSize: "0.875rem", margin: 0 }}>
              این ۱۰ کد فقط همین یک‌بار نمایش داده می‌شوند. لطفاً آن‌ها را چاپ کرده و در جای امن نگهداری
              نمایید.
            </p>
            <div
              className="grid"
              style={{
                gridTemplateColumns: "1fr 1fr",
                gap: "var(--s2)",
                fontFamily: "monospace",
                direction: "ltr",
                textAlign: "center",
                padding: "var(--s3)",
                background: "var(--bg-subtle, #f5f5f5)",
                borderRadius: "var(--r1)",
              }}
            >
              {newCodes.map((c, i) => (
                <div
                  key={i}
                  style={{
                    padding: "var(--s2)",
                    border: "1px dashed var(--border, #ccc)",
                    borderRadius: "var(--r0)",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                  }}
                >
                  {c}
                </div>
              ))}
            </div>
            <div
              className="row"
              style={{ gap: "var(--s2)", justifyContent: "flex-end", marginTop: "var(--s3)" }}
            >
              <Button
                variant="default"
                onClick={() => {
                  window.print();
                }}
              >
                چاپ برگه A4
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setNewCodes(null);
                }}
              >
                متوجه شدم و ذخیره کردم
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
